import {useCallback, useEffect, useState} from 'react';
import {
    Box,
    Button,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Skeleton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {useTheme} from '@mui/material/styles';
import TablePagination from '@mui/material/TablePagination';
import {InputField, SnackbarField} from '@/components/common';
import {AuditFsAnnualService, AnnualFsStatusEnum} from '@/services/auditService';
import C7MaterialitySummary from '@/pages/components/audit-program/C7-MaterialitySummary';
import C71DeterminingMateriality from '@/pages/components/audit-program/C71-DeterminingMateriality';
import {
    C71VO,
    formatAmount,
    getC71VO,
    performanceMateriality,
} from '@/services/auditService/materialityService';

interface CompanyRow {
    companyId: number;
    annualId: number;
    companyName: string;
    yearOfAssessment: string;
}

type DetailView = 'c7' | 'c7-1';

export default function C7List() {
    const theme = useTheme();
    const [detailView, setDetailView] = useState<DetailView>('c7');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<CompanyRow[]>([]);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [total, setTotal] = useState(0);
    const [filterCompanyName, setFilterCompanyName] = useState('');
    const [selected, setSelected] = useState<CompanyRow | null>(null);
    const [c71, setC71] = useState<C71VO | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message?: string; severity?: 'success' | 'error' }>({
        open: false,
    });

    const loadCompanies = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await AuditFsAnnualService.queryPage(AnnualFsStatusEnum.AUDIT_ADJUSTMENT, {
                pageNumber: pageNum,
                pageSize,
                pageType: 1,
                companyName: filterCompanyName || undefined,
            });
            const list = (resp?.list || []) as CompanyRow[];
            setRows(list);
            setTotal(Number(resp?.totalRow || 0));
            setError(null);
        } catch (e: any) {
            setError(e?.message || 'Load failed');
        } finally {
            setLoading(false);
        }
    }, [pageNum, pageSize, filterCompanyName]);

    useEffect(() => {
        const handler = setTimeout(loadCompanies, 400);
        return () => clearTimeout(handler);
    }, [loadCompanies]);

    useEffect(() => {
        setDetailView('c7');
        if (!selected) {
            setC71(null);
            return;
        }
        let cancelled = false;
        (async () => {
            setDetailLoading(true);
            try {
                const data = await getC71VO(selected.companyId, selected.annualId);
                if (!cancelled) setC71(data);
            } catch (e: any) {
                if (!cancelled) {
                    setSnackbar({open: true, message: e?.message || 'Failed to load C7 data', severity: 'error'});
                    setC71(null);
                }
            } finally {
                if (!cancelled) setDetailLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selected]);

    // Excel C7: Initial=F5=C7.1!D16 (Turnover mat), Final=I5=C7.1!G16 (Turnover final mat)
    const overallInitial = Number(c71?.turnMatLevel ?? 0);
    const overallFinal = Number(c71?.turnFinMatLevel ?? 0);

    return (
        <Box sx={{p: '20px'}}>
            <Box sx={{
                height: 'calc(100vh - 120px)',
                minHeight: '70vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexShrink={0}>
                    <Typography variant="h6" fontWeight={700}>
                        C7 — Materiality Summary
                    </Typography>
                    <InputField
                        size="small"
                        label="Company Name"
                        value={filterCompanyName}
                        onChange={e => {
                            setPageNum(1);
                            setFilterCompanyName(e.target.value);
                        }}
                    />
                </Stack>

                {error && (
                    <Box sx={{color: 'error.main', px: 1, py: 0.5, fontSize: 12, mb: 1, flexShrink: 0}}>{error}</Box>
                )}

                <TableContainer
                    component={Paper}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        boxShadow: theme.shadows[2],
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            '& .MuiTableCell-stickyHeader': {
                                bgcolor: 'background.paper',
                                zIndex: 3,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>Company Name</TableCell>
                                <TableCell>Year Of Assessment</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading
                                ? Array.from({length: 5}).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={2}><Skeleton/></TableCell>
                                    </TableRow>
                                ))
                                : rows.map(row => (
                                    <TableRow
                                        key={`${row.companyId}-${row.annualId}`}
                                        hover
                                        selected={selected?.annualId === row.annualId}
                                        sx={{cursor: 'pointer'}}
                                        onClick={() => setSelected(row)}
                                    >
                                        <TableCell>{row.companyName}</TableCell>
                                        <TableCell>{row.yearOfAssessment}</TableCell>
                                    </TableRow>
                                ))}
                            {!loading && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={2} align="center">No data</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={total}
                    page={pageNum - 1}
                    onPageChange={(_, p) => setPageNum(p + 1)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={e => {
                        setPageSize(parseInt(e.target.value, 10) || 25);
                        setPageNum(1);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{flexShrink: 0}}
                />
            </Box>

            {selected && (
                <Box mt={3}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {selected.companyName} — {selected.yearOfAssessment}
                            {detailView === 'c7-1' ? ' · C7.1' : ' · C7'}
                        </Typography>
                        {detailView === 'c7' ? (
                            <Button
                                variant="outlined"
                                size="small"
                                endIcon={<ArrowForwardIcon/>}
                                disabled={detailLoading || !c71}
                                onClick={() => setDetailView('c7-1')}
                            >
                                C7.1 — Determining Materiality
                            </Button>
                        ) : (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ArrowBackIcon/>}
                                onClick={() => setDetailView('c7')}
                            >
                                Back to C7
                            </Button>
                        )}
                    </Stack>
                    {detailLoading ? (
                        <Skeleton variant="rectangular" height={480}/>
                    ) : detailView === 'c7-1' && c71 ? (
                        <C71DeterminingMateriality data={c71}/>
                    ) : (
                        <C7MaterialitySummary
                            client={c71?.client || selected.companyName}
                            yearEnd={c71?.yearEnd}
                            preparedBy={c71?.preparedBy}
                            reviewedBy={c71?.reviewedBy}
                            overallInitial={formatAmount(overallInitial)}
                            overallFinal={formatAmount(overallFinal)}
                            performanceBySection={{
                                ...Object.fromEntries(
                                    ['E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'].map(code => [
                                        code,
                                        {
                                            initial: formatAmount(performanceMateriality(overallInitial, 1.2)),
                                            final: formatAmount(performanceMateriality(overallFinal, 1.2)),
                                        },
                                    ])
                                ),
                                'R-income': {
                                    initial: formatAmount(performanceMateriality(overallInitial, 1.6)),
                                    final: formatAmount(performanceMateriality(overallFinal, 1.6)),
                                },
                                'R-expenses': {
                                    initial: formatAmount(performanceMateriality(overallInitial, 1.2)),
                                    final: formatAmount(performanceMateriality(overallFinal, 1.2)),
                                },
                                'R-directors': {
                                    initial: formatAmount(performanceMateriality(overallInitial, 1.2)),
                                    final: formatAmount(performanceMateriality(overallFinal, 1.2)),
                                },
                                'R-related': {
                                    initial: formatAmount(performanceMateriality(overallInitial, 1.2)),
                                    final: formatAmount(performanceMateriality(overallFinal, 1.2)),
                                },
                            }}
                        />
                    )}
                </Box>
            )}

            <SnackbarField
                open={snackbar.open}
                message={snackbar.message ?? ''}
                severity={snackbar.severity}
                onClose={() => setSnackbar({open: false})}
            />
        </Box>
    );
}
